<template>
  <div class="multiselect-main" :class="{ 'is-disabled': isDisabled }">
    <div class="multiselect-field" :class="{ 'is-disabled': isDisabled, 'is-compact': isCompact, 'has-selections': value.length > 0, 'is-placeholder-floated': value.length > 0 || inputFocused }">
      <Dropdown
        ref="dropdown"
        class="multiselect-dropdown"
        :items="availableItems"
        :itemKey="itemKey"
        :selectedObject.sync="selectedObject"
        :search.sync="searchText"
        :placeholder="placeholder"
        :isRequired="isRequired"
        :isDisabled="isDisabled"
        :errorMessage.sync="privateErrorMessage"
        @selected="addItem"
        @focusChanged="inputFocusChanged"
        @keydown="dropdownKeyDown"
      >
        <template slot="prefix">
          <div v-show="value.length > 0" class="multiselect-chips" ref="chipList">
            <button
              v-for="(selectedValue, index) in visibleSelectedItems"
              :key="`chip-${index}`"
              type="button"
              class="multiselect-chip"
              :title="getSelectedOptionTitle(selectedValue)"
              :aria-label="'Remove ' + getSelectedOptionLabel(selectedValue)"
              :disabled="isDisabled"
              @mousedown.prevent
              @click="removeItem(selectedValue)"
            >
              <span class="multiselect-chip-label">{{ getSelectedOptionLabel(selectedValue) }}</span>
              <span v-if="!isDisabled" class="multiselect-chip-remove" aria-hidden="true">×</span>
            </button>
            <button
              v-if="hiddenSelectedItems.length > 0"
              type="button"
              class="multiselect-chip multiselect-chip-overflow"
              :title="getHiddenItemsTitle()"
              :disabled="isDisabled"
              @mousedown.prevent
              @click="openTable"
            >+{{ hiddenSelectedItems.length }}</button>
          </div>
        </template>
        <template slot="list-caption">
          <span v-if="value.length > 1 && !isDisabled" class="multiselect-list-caption">
            {{ value.length }} selected &middot;
            <a class="multiselect-list-clear" @mousedown.prevent @click="clearAll">Clear all</a>
          </span>
        </template>
        <template slot="label-suffix">
          <span v-if="value.length > 0" class="multiselect-count">{{ value.length }}</span>
        </template>
      </Dropdown>
      <button
        v-if="value.length > 1 && !isDisabled"
        type="button"
        class="multiselect-clear-btn"
        :title="`Clear ${value.length} selections (Shift + Backspace)`"
        :aria-label="`Clear all ${value.length} ${placeholder} selections`"
        @mousedown.prevent
        @click="clearAll"
      >
        <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 2.5l7 7M9.5 2.5l-7 7" /></svg>
      </button>
      <button
        type="button"
        class="multiselect-query-btn"
        :disabled="isDisabled"
        tabindex="-1"
        title="Inspect options (Ctrl + Q)"
        @mousedown.prevent
        @click="openTable"
      >
        <img src="https://dsmlib.logistics.systems/Icons/MagnifyingGlass.png" alt="" />
      </button>
    </div>

    <div class="multiselect-chip-measurements" aria-hidden="true">
      <span v-for="(selectedValue, index) in value" :key="`measure-${index}`" ref="chipMeasurements" class="multiselect-chip">
        <span class="multiselect-chip-label">{{ getSelectedOptionLabel(selectedValue) }}</span>
        <button v-if="!isDisabled" type="button" class="multiselect-chip-remove">×</button>
      </span>
      <span ref="overflowChipMeasurement" class="multiselect-chip multiselect-chip-overflow">+{{ value.length }}</span>
    </div>

    <div v-if="showTable" ref="tablePopup" class="table-popup">
      <div class="content">
        <TableBox
          :tableData="tableItems"
          :title="`Inspect ${placeholder} options`"
          :selectedRow.sync="tableSelectedRows"
          :maxSelection="-1"
          :showSelectAll="true"
          :fitContentWidth="true"
          :hideColumns="tableBoxHideColumns"
        />
        <div class="button-container">
          <button type="button" @click="confirmTable">Confirm {{ tableSelectedRows.length }} items</button>
          <button type="button" class="cancel-button" :disabled="isDisabled || !tableSelectedRows.length" @click="tableSelectedRows = []">Clear All</button>
          <button type="button" class="cancel-button" @click="closeTable">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data: function () {
    return {
      selectedObject: null,
      searchText: '',
      inputFocused: false,
      showTable: false,
      tableSelectedRows: [],
      teleportedTable: null,
      visibleChipCount: 0,
      privateErrorMessage: this.errorMessage || '',
      resizeObserver: null,
    };
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Array,
      default: () => [],
    },
    itemKey: {
      type: String,
      default: 'label',
    },
    saveKey: {
      type: String,
      default: 'key',
    },
    placeholder: {
      type: String,
      default: 'Add...',
    },
    itemDesc: {
      type: String,
      default: '',
    },
    tableBoxHideColumns: {
      type: Array,
      default: () => [],
    },
    isRequired: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isCompact: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    tableItems() {
      return this.items.map((item, index) => Object.assign({ '#': index + 1 }, item, { '#': index + 1 }));
    },
    availableItems() {
      return this.items.filter(
        (item) => !this.value.some((selectedItem) => this.optionValueMatches(this.getSelectedValue(selectedItem), item[this.saveKey])),
      );
    },
    visibleSelectedItems() {
      return this.isCompact ? this.value.slice(0, this.visibleChipCount) : this.value;
    },
    hiddenSelectedItems() {
      return this.isCompact ? this.value.slice(this.visibleChipCount) : [];
    },
  },
  methods: {
    hasValue(value) {
      return value !== null && value !== undefined && value !== '';
    },
    optionValueMatches(optionValue, savedValue) {
      if (!this.hasValue(optionValue) || !this.hasValue(savedValue)) return optionValue === savedValue;
      return String(optionValue) === String(savedValue);
    },
    getSelectedValue(item) {
      return item && typeof item == 'object' ? item[this.saveKey] : item;
    },
    normalizeSelectedItem(item) {
      if (item && typeof item == 'object') return item;
      return this.items.find((option) => this.optionValueMatches(option[this.saveKey], item)) || {
        [this.saveKey]: item,
        [this.itemKey]: item,
      };
    },
    getSelectedOption(item) {
      if (item && typeof item == 'object') return item;
      return this.items.find((option) => this.optionValueMatches(option[this.saveKey], item));
    },
    getSelectedOptionLabel(item) {
      if (item && typeof item == 'object') return item[this.itemKey] ?? item[this.saveKey] ?? '';
      const option = this.getSelectedOption(item);
      return option?.[this.itemKey] ?? item;
    },
    getSelectedOptionTitle(item) {
      const option = this.getSelectedOption(item);
      if (!option) return this.getSelectedOptionLabel(item);

      const details = [];
      if (this.itemDesc && this.hasValue(option[this.itemDesc])) {
        details.push(`${this.itemDesc}: ${option[this.itemDesc]}`);
      }
      Object.keys(option).forEach((key) => {
        if (['label', 'key', 'value', this.itemDesc].includes(key) || !this.hasValue(option[key])) return;
        details.push(`${key}: ${option[key]}`);
      });
      return [this.getSelectedOptionLabel(option), ...details].join('\n');
    },
    addItem(item) {
      if (!item || this.isDisabled) return;
      const value = item[this.saveKey];
      if (this.value.some((selectedItem) => this.optionValueMatches(this.getSelectedValue(selectedItem), value))) return;
      this.$emit('update:value', [...this.value.map((selectedItem) => this.normalizeSelectedItem(selectedItem)), item]);
      this.clearError();
      this.$nextTick(() => {
        this.selectedObject = null;
        this.searchText = '';
      });
    },
    removeItem(item) {
      if (this.isDisabled) return;
      const value = this.getSelectedValue(item);
      this.$emit(
        'update:value',
        this.value
          .map((selectedItem) => this.normalizeSelectedItem(selectedItem))
          .filter((selectedItem) => !this.optionValueMatches(this.getSelectedValue(selectedItem), value)),
      );
      this.clearError();
    },
    clearAll() {
      if (this.isDisabled || !this.value.length) return;
      this.selectedObject = null;
      this.searchText = '';
      this.tableSelectedRows = [];
      this.$emit('update:value', []);
      this.clearError();
    },
    clearError() {
      this.privateErrorMessage = '';
    },
    getHiddenItemsTitle() {
      return this.hiddenSelectedItems.map((item) => this.getSelectedOptionLabel(item)).join('\n');
    },
    inputFocusChanged(focused) {
      this.inputFocused = focused;
    },
    dropdownKeyDown(event) {
      if (event.ctrlKey && event.key && event.key.toUpperCase() === 'Q') {
        event.preventDefault();
        this.openTable();
        return;
      }
      if (event.key === 'Backspace' && !this.searchText && this.value.length > 0 && !this.isDisabled) {
        event.preventDefault();
        if (event.shiftKey) {
          this.clearAll(); // Shift + Backspace clears everything
          return;
        }
        this.removeItem(this.value[this.value.length - 1]);
      }
    },
    openTable() {
      if (this.isDisabled) return;
      this.tableSelectedRows = this.value.map((selectedItem) => {
        const value = this.getSelectedValue(selectedItem);
        return this.tableItems.find((item) => this.optionValueMatches(item[this.saveKey], value)) || this.normalizeSelectedItem(selectedItem);
      });
      this.showTable = true;
      this.$nextTick(() => {
        if (this.$refs.dropdown) this.$refs.dropdown.setBlur();
      });
    },
    confirmTable() {
      const selectedRows = this.tableSelectedRows.map((item) => {
        const value = this.getSelectedValue(item);
        const option = this.items.find((candidate) => this.optionValueMatches(candidate[this.saveKey], value));
        return option || this.normalizeSelectedItem(item);
      });
      this.showTable = false;
      this.clearError();
      this.$nextTick(() => {
        this.$emit('update:value', selectedRows);
      });
    },
    closeTable() {
      this.showTable = false;
    },
    attachTableTeleport() {
      const table = this.$refs.tablePopup;
      if (!table || this.teleportedTable) return;
      table.style.position = 'fixed';
      table.style.zIndex = '99990';
      this.$getOverlayRoot().appendChild(table);
      this.teleportedTable = table;
    },
    detachTableTeleport() {
      if (this.teleportedTable && this.teleportedTable.parentNode) {
        this.teleportedTable.parentNode.removeChild(this.teleportedTable);
      }
      this.teleportedTable = null;
    },
    escapeCloseTable(event) {
      if (event.code === 'Escape') this.closeTable();
    },
    observeChipField() {
      const control = this.$refs.dropdown && this.$refs.dropdown.$el.querySelector('.input-control');
      if (!control || this.observedChipField === control) return;
      this.observedChipField = control;
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(this.remeasureChips);
        this.resizeObserver.observe(control);
      } else {
        window.addEventListener('resize', this.remeasureChips);
      }
      this.remeasureChips();
    },
    remeasureChips() {
      if (!this.isCompact) {
        this.visibleChipCount = this.value.length;
        return;
      }
      if (!this.value.length) {
        this.visibleChipCount = 0;
        return;
      }
      const control = this.$refs.dropdown && this.$refs.dropdown.$el.querySelector('.input-control');
      const chipNodes = this.$refs.chipMeasurements || [];
      const overflowChip = this.$refs.overflowChipMeasurement;
      if (!control || chipNodes.length !== this.value.length || !overflowChip) return;

      const availableWidth = Math.max(0, control.clientWidth - 43);
      const chipWidths = chipNodes.map((chip) => chip.getBoundingClientRect().width);
      const gap = 3;
      let visibleCount = this.value.length;
      const chipWidth = chipWidths.reduce((total, width) => total + width, 0) + Math.max(0, this.value.length - 1) * gap;
      if (chipWidth > availableWidth) {
        visibleCount = 0;
        for (let candidate = this.value.length - 1; candidate >= 0; candidate -= 1) {
          const hiddenCount = this.value.length - candidate;
          overflowChip.textContent = `+${hiddenCount}`;
          const overflowWidth = overflowChip.getBoundingClientRect().width;
          const candidateWidth = chipWidths.slice(0, candidate).reduce((total, width) => total + width, 0) + Math.max(0, candidate - 1) * gap;
          const requiredWidth = candidateWidth + overflowWidth + (candidate > 0 ? gap : 0);
          if (requiredWidth <= availableWidth || candidate === 0) {
            visibleCount = candidate;
            break;
          }
        }
      }
      overflowChip.textContent = `+${this.value.length}`;
      this.visibleChipCount = visibleCount;
    },
  },
  mounted() {
    this.$nextTick(this.observeChipField);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.escapeCloseTable);
    window.removeEventListener('resize', this.remeasureChips);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.detachTableTeleport();
  },
  watch: {
    value: {
      handler() {
        this.$nextTick(this.remeasureChips);
      },
      deep: true,
    },
    items() {
      this.$nextTick(this.remeasureChips);
    },
    isDisabled() {
      this.$nextTick(this.remeasureChips);
    },
    isCompact() {
      this.$nextTick(this.remeasureChips);
    },
    errorMessage(value) {
      this.privateErrorMessage = value;
    },
    privateErrorMessage(value) {
      this.$emit('update:errorMessage', value);
    },
    showTable(value) {
      if (value) {
        document.addEventListener('keydown', this.escapeCloseTable);
        this.$nextTick(() => this.attachTableTeleport());
      } else {
        document.removeEventListener('keydown', this.escapeCloseTable);
        this.detachTableTeleport();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.multiselect-main {
  width: 100%;
  position: relative;
}

.multiselect-field {
  display: flex;
  align-items: center;
  width: 100%;
  height: 25px;
  margin: 2px 0;

  > .multiselect-dropdown {
    flex: 1 1 auto;
    min-width: 0;
    width: auto;
    height: 25px;
    margin: 0;
  }

  &:not(.is-compact) {
    height: auto;
    min-height: 25px;
    align-items: flex-end;

    > .multiselect-dropdown {
      height: auto;
      min-height: 25px; // same resting height as a normal Dropdown
    }
  }

  .multiselect-chips {
    display: flex;
    flex: 0 1 auto;
    align-items: flex-end;
    height: 100%;
    gap: 3px;
    min-width: 0;
    max-width: calc(100% - 43px);
    overflow: hidden;
    white-space: nowrap;
  }

  &:not(.is-compact) .multiselect-chips {
    display: contents;
  }

  &.is-disabled .multiselect-chip {
    background-color: rgba(200, 200, 200, 0.35); // borderless chips need a fill to stay visible when disabled
    color: #444;
  }

  .multiselect-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 11px;
    height: 11px;
    margin-left: 4px;
    padding: 0 4px;
    border-radius: 6px;
    background-color: $btn-color;
    color: black;
    font-size: 8px;
    line-height: 11px;
  }

  .multiselect-clear-btn {
    // Small round × beside the magnifier, revealed on hover / focus so it takes no attention (or chip space) at rest
    display: flex;
    flex: 0 0 15px;
    align-items: center;
    justify-content: center;
    width: 15px;
    height: 15px;
    margin: 0 3px 0 0;
    padding: 0;
    position: relative;
    top: -3px;
    border: 0;
    border-radius: 50%;
    background-color: transparent;
    color: #777;
    cursor: pointer;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.12s, background-color 0.12s, color 0.12s;

    > svg {
      display: block;
      width: 9px;
      height: 9px;
      fill: none;
      stroke: currentColor;
      stroke-width: 1.8;
      stroke-linecap: round;
    }

    &:hover {
      background-color: #fdecec; // red tint signals a destructive action (beats the global button hover colour)
      color: #c62828;
    }

    &:focus {
      box-shadow: none;
    }

    &:focus-visible {
      opacity: 1;
      pointer-events: auto;
      box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.3);
    }
  }

  &:hover > .multiselect-clear-btn,
  &:focus-within > .multiselect-clear-btn {
    opacity: 1;
    pointer-events: auto;
  }

  .multiselect-query-btn {
    display: flex;
    flex: 0 0 15px;
    align-items: center;
    justify-content: center;
    width: 15px;
    height: 15px;
    margin: 0 2px 0 0;
    padding: 0;
    position: relative;
    top: -3px;
    border: 0;
    background: transparent;
    cursor: pointer;

    > img {
      display: block;
      width: 15px;
      height: 15px;
    }

    &:disabled {
      opacity: 0.35;
      cursor: default;
    }
  }
}

// Lives inside DropdownV2's list caption, which is teleported to the overlay root, so keep these top-level
.multiselect-list-caption {
  margin-right: auto;
  color: #555;
  white-space: nowrap;
}

.multiselect-list-clear {
  color: #c62828;
  text-decoration: underline;
  cursor: pointer;
}

.multiselect-chip-measurements {
  position: absolute;
  top: 0;
  left: -10000px;
  display: flex;
  gap: 3px;
  width: max-content;
  visibility: hidden;
  pointer-events: none;

}

.multiselect-field:not(.is-compact) > .multiselect-dropdown ::v-deep .dropdown-input {
  height: auto;
  min-height: 25px; // keeps label + underline where Dropdown/Input put them; grows only when chips wrap
}

.multiselect-field > .multiselect-dropdown ::v-deep .placeholder-top,
.multiselect-field.is-placeholder-floated > .multiselect-dropdown ::v-deep .placeholder {
  top: -12px;
  height: 25px; // same box as InputV2's floated label, so it lands at the same height as Dropdown/Input labels
  align-items: center;
  font-size: 8px;
  opacity: 1;
}

.multiselect-field > .multiselect-dropdown ::v-deep .input-control {
  display: flex;
  align-items: flex-end;
  position: relative;
  top: -3px;
  width: 100%;
  height: 24px; // Match the rendered height of the standard underlined input.
  border-bottom: 1px solid rgba(128, 128, 128, 0.5);

  &.is-disabled {
    background-color: rgba(200, 200, 200, 0.2);
  }

  > input[type='text'],
  > input[type='password'],
  > input[type='number'] {
    flex: 1 1 40px;
    min-width: 40px;
    width: 0;
    height: 100%;
    position: static;
    border: 0;
    padding: 0 0 0 2px;
    background-color: transparent;
    transition: none;
  }

  > input:focus {
    outline: none;
  }

  &:focus-within {
    border-color: black;
  }

  > input:disabled {
    background-color: transparent;
  }
}

.multiselect-field:not(.is-compact) > .multiselect-dropdown ::v-deep .input-control {
  height: auto;
  min-height: 24px; // match InputV2's standard underlined input height (single row = exactly a DropdownV2)
  padding-top: 7px; // 7 + 16px chip row + 1px underline = 24px; also clears the floated label when rows wrap
  padding-left: 5px; // chip text (5px chip padding) starts at 10px, same as DropdownV2's text and label
  margin-bottom: 1px; // underline lands on the same row as Dropdown/Input underlines
  flex-wrap: wrap;
  gap: 3px;
}

.multiselect-field > .multiselect-dropdown ::v-deep .input-control > input {
  height: 16px !important; // same height as a chip row, so typing never makes a row taller
  padding-left: 5px !important; // typed text starts at 10px like DropdownV2 when there are no chips
  font-size: 12px; // same as DropdownV2's value text
}

.multiselect-field:not(.is-compact) > .multiselect-clear-btn,
.multiselect-field:not(.is-compact) > .multiselect-query-btn {
  top: -7px; // vertically centred with the dropdown arrow
}

.multiselect-field:not(.is-compact) > .multiselect-dropdown ::v-deep .arrow-container {
  top: auto;
  bottom: 5px; // same spot as Dropdown's arrow on a single row; follows the last row when chips wrap
}

.multiselect-field .multiselect-chip,
.multiselect-chip-measurements > .multiselect-chip {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0;
  position: relative; // anchors the hover-only ×
  box-sizing: border-box;
  max-width: 100%;
  height: 16px; // fills the value area between the floated label and the underline
  padding: 0 5px;
  border: 0; // borderless solid fill reads cleaner at this size
  border-radius: 3px;
  background-color: #dbe2ff;
  appearance: none;
  -webkit-appearance: none;
  color: #111;
  cursor: pointer;
  font-size: 12px; // same as DropdownV2's value text
  font-family: inherit;
  font-weight: normal;
  line-height: 1;
  text-align: left;
  white-space: nowrap;

  &:disabled {
    opacity: 1;
    cursor: default;
  }
}

.multiselect-field .multiselect-chip-label,
.multiselect-chip-measurements .multiselect-chip-label {
  min-width: 0;
  max-width: none;
  overflow: hidden; // full text normally; ellipsis only if a chip is wider than the whole field
  text-overflow: ellipsis;
  white-space: nowrap;
}

.multiselect-field .multiselect-chip-remove,
.multiselect-chip-measurements .multiselect-chip-remove {
  // Slim × shown only on hover/keyboard focus. It sits on top of the chip's right end
  // (absolute, takes no width), so chips stay compact and never reflow when hovered.
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  right: 2px;
  width: 12px;
  height: 12px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 50%;
  transform: translateY(-50%);
  background-color: #dbe2ff; // same as the chip, so it cleanly covers the text end
  box-shadow: -3px 0 3px #dbe2ff; // soft fade over the last letter
  color: #555;
  cursor: pointer;
  font-size: 11px;
  line-height: 1;
  opacity: 0;
  pointer-events: none;

}

.multiselect-field .multiselect-chip:hover:enabled {
  background-color: #c9d3fb; // slightly darker on hover (overrides the global button hover colour)
}

.multiselect-field .multiselect-chip:hover > .multiselect-chip-remove,
.multiselect-field .multiselect-chip:focus-visible > .multiselect-chip-remove {
  opacity: 1;
  pointer-events: auto;
  background-color: #c9d3fb; // matches the hovered chip
  box-shadow: -3px 0 3px #c9d3fb;
}

.multiselect-field .multiselect-chip-overflow,
.multiselect-chip-measurements .multiselect-chip-overflow {
  height: 16px;
  border: 1px dashed rgba(120, 140, 220, 0.6);
  padding: 0 5px;
  border-style: dashed;
  background-color: #fff;
  color: #333;
  cursor: pointer;
}

.table-popup {
  position: fixed;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 99990;
  padding: 10px;
  padding-top: calc(#{$top-nav-height} + 10px);
  background-color: rgba(0, 0, 0, 0.3);

  > .content {
    position: relative;
    box-sizing: border-box;
    width: 90%;
    height: 70%;
    padding: 10px;
    border-radius: 5px;
    background-color: white;

    > .button-container {
      position: absolute;
      top: 100%;
      right: 10px;
      display: flex;
      width: fit-content;
      margin: 0 3px;

      > button {
        border-radius: 0;
        border-bottom-right-radius: 5px;
        border-bottom-left-radius: 5px;
        background-color: white;

        &:first-child {
          color: green;
        }

        &:last-child {
          margin-left: 20px;
          color: red;
        }
      }
    }
  }
}
</style>
